import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import TravelRiderCard from "../TravelRiderCard";
import TechRiderLink from "../TechRiderLink";
import { resolveTravelTerms, travelCostDisclosure } from "../../lib/travelTerms";
import { track } from "../../analytics/tracker";

export function buildOutstationNotice(pkg, rider) {
  const requirements = [];
  if (pkg?.travel_required || rider?.travel_modes?.length || rider?.flight_class) requirements.push("travel");
  if (pkg?.accommodation_required || rider?.hotel_required || rider?.hotel_category) requirements.push("accommodation");
  if (pkg?.local_transport_required || rider?.local_transport) requirements.push("local transportation");
  if (pkg?.meals_required || rider?.food_preference || rider?.hospitality_needs?.length) requirements.push("meals and hospitality");

  const requirementText = requirements.length
    ? `The artist has requested ${requirements.join(", ")}.`
    : "The artist has not specified particular travel requirements yet.";
  const costText = travelCostDisclosure(pkg, rider);
  const notes = [pkg?.travel_notes, rider?.travel_notes, rider?.additional_conditions]
    .filter(Boolean)
    .join(" ");

  return [
    `${requirementText} ${costText}`,
    notes ? `Artist notes: ${notes}` : "",
  ].filter(Boolean).join(" ");
}

/**
 * Iter 50 — ReviewStep, updated in Iter 52.5/52.7:
 *   • Mandatory Terms & Conditions declaration checkbox — required before
 *     "Proceed to Payment" is enabled.
 */
export default function ReviewStep({
  pkg,
  form,
  set,
  isOutstation,
  eventItems = [],
  onBack,
  onNext,
  artistId,
  techRiderFile,
}) {
  // Iter 52.7 — pull the artist's Travel & Hospitality rider straight from
  // their onboarding questionnaire so the customer can see everything they'll
  // need to arrange BEFORE hitting Proceed to Payment. No new questions are
  // asked; we just read profile.answers via /artists/{id}/quote?city=…
  const [rider, setRider] = useState(null);
  const [quoteTechRiderFile, setQuoteTechRiderFile] = useState(null);
  const [techRiderPreviewError, setTechRiderPreviewError] = useState("");
  const [openingTechRider, setOpeningTechRider] = useState(false);
  const [riderLoading, setRiderLoading] = useState(false);
  useEffect(() => {
    if (!isOutstation || !artistId || !form?.city) {
      setRider(null);
      setQuoteTechRiderFile(null);
      setTechRiderPreviewError("");
      setRiderLoading(false);
      return;
    }
    let active = true;
    setRiderLoading(true);
    api.get(`/artists/${artistId}/quote?city=${encodeURIComponent(form.city)}`)
      .then((r) => {
        if (active) {
          setRider(r.data?.rider || null);
          setQuoteTechRiderFile(r.data?.tech_rider_file || null);
          setTechRiderPreviewError("");
        }
      })
      .catch(() => {
        if (active) {
          setRider(null);
          setQuoteTechRiderFile(null);
        }
      })
      .finally(() => { if (active) setRiderLoading(false); });
    return () => { active = false; };
  }, [isOutstation, artistId, form?.city]);

  const previewTechRider = async () => {
    setTechRiderPreviewError("");
    const previewWindow = window.open("about:blank", "_blank");
    if (!previewWindow) {
      setTechRiderPreviewError("Allow pop-ups to preview the Tech Rider PDF.");
      return;
    }
    previewWindow.opener = null;
    setOpeningTechRider(true);
    try {
      const response = await api.get(
        `/artist/tech-rider/${encodeURIComponent(artistId)}/download`,
        { responseType: "blob" },
      );
      const pdfUrl = URL.createObjectURL(response.data);
      previewWindow.location.href = pdfUrl;
    } catch (error) {
      previewWindow.close();
      setTechRiderPreviewError(`Could not open the Tech Rider PDF: ${formatApiError(error)}`);
    } finally {
      setOpeningTechRider(false);
    }
  };

  const showTravelRider =
    pkg && (pkg.travel_required || pkg.accommodation_required || pkg.local_transport_required || pkg.meals_required || pkg.travel_notes || pkg.travel_charge_model);

  const displayRider = rider ? {
    ...rider,
    travel_modes: pkg?.travel_terms_defined ? pkg.travel_modes : rider.travel_modes,
    flight_class: pkg?.travel_terms_defined ? pkg.flight_class : rider.flight_class,
    train_class: pkg?.travel_terms_defined ? pkg.train_class : rider.train_class,
    hotel_required: pkg?.travel_terms_defined ? (pkg.accommodation_required ? "Required" : "No") : rider.hotel_required,
    hotel_category: pkg?.travel_terms_defined ? pkg.hotel_category : rider.hotel_category,
    travel_party_size: pkg?.travel_terms_defined ? pkg.team_size : rider.travel_party_size,
    travel_charge_model: pkg?.travel_terms_defined ? pkg.travel_charge_model : rider.travel_charge_model,
    travel_flat_fee: pkg?.travel_terms_defined ? pkg.travel_flat_fee : rider.travel_flat_fee,
    travel_free_distance_km: pkg?.travel_terms_defined ? pkg.travel_free_distance_km : rider.travel_free_distance_km,
    travel_per_km_fee: pkg?.travel_terms_defined ? pkg.travel_per_km_fee : rider.travel_per_km_fee,
    travel_notes: pkg?.travel_terms_defined ? pkg.travel_notes : rider.travel_notes,
  } : null;
  const visibleTechRiderFile = techRiderFile || quoteTechRiderFile;

  const policyItems = eventItems.length
    ? eventItems
    : [{ is_primary: true, is_outstation: Boolean(isOutstation), travel_package: pkg }];
  const travelPolicyRows = policyItems
    .filter((item) => item.is_outstation)
    .map((item) => {
      const itemPackage = item.travel_package || (item.is_primary ? pkg : {});
      const itemRider = item.is_primary ? displayRider || rider || {} : item.travel_rider || {};
      return {
        key: item.artist_id || itemPackage.id || item.artist_name,
        artistName: item.artist_name || "Selected artist",
        packageName: item.package_name || itemPackage.name,
        package: itemPackage,
        rider: itemRider,
        terms: resolveTravelTerms(itemPackage, itemRider),
      };
    });
  const eventHasOutstation = travelPolicyRows.length > 0;
  const requiresTravelTermsAck = travelPolicyRows.some((row) => row.terms.requiresAcknowledgement);
  const travelTermsAcknowledged = Boolean(
    form.travel_terms_acknowledged || form.travel_ack || form.outstation_ack,
  );

  // Only collect acknowledgement for outstation terms that may add direct costs.
  const nextDisabled =
    !form.tnc_accepted ||
    riderLoading ||
    (requiresTravelTermsAck && !travelTermsAcknowledged);
  const handleNext = () => {
    if (nextDisabled) {
      track("booking_validation_error", {
        step: 4,
        field_names: [
          ...(!form.tnc_accepted ? ["tnc_accepted"] : []),
          ...(requiresTravelTermsAck && !travelTermsAcknowledged ? ["travel_terms_acknowledged"] : []),
        ],
      });
      return;
    }
    onNext();
  };

  return (
    <div className="card card-pad" data-testid="step-4">
      <h2 className="font-serif fs-20 fw-700 mb-8">Review & Confirm</h2>
      <p className="text-muted fs-13 mb-20">Double-check details before payment.</p>

      <div className="card card-pad mb-16">
        <h3 className="fw-600 fs-13 mb-12 text-gold" style={{ textTransform: "uppercase" }}>Performance Package</h3>
        <div className="flex justify-between mb-8"><span className="text-muted">Package</span><span>{pkg?.name}</span></div>
        <div className="flex justify-between mb-8"><span className="text-muted">Duration</span><span>{pkg?.duration}</span></div>
        <div className="flex justify-between mb-8"><span className="text-muted">Add-ons</span><span>{form.addons.length || "None"}</span></div>
        {form.addon_selections.length > 0 && (
          <div className="flex justify-between"><span className="text-muted">Artist add-ons</span><span data-testid="review-artist-addons">{form.addon_selections.length}</span></div>
        )}
      </div>

      {visibleTechRiderFile?.mime === "application/pdf" && (
        <div className="card card-pad mb-16" data-testid="review-tech-rider">
          <h3 className="fw-600 fs-13 mb-8 text-gold" style={{ textTransform: "uppercase" }}>Production information</h3>
          <p className="text-muted fs-12 mb-12">
            Review the artist’s technical requirements before confirming the booking.
          </p>
          <TechRiderLink
            artistId={artistId}
            filename={visibleTechRiderFile.filename || "Tech Rider PDF"}
            className="btn btn-ghost"
            testId="review-tech-rider-link"
          />
        </div>
      )}

      {showTravelRider && (
        <div className="card card-pad mb-16" data-testid="review-travel-block">
          <h3 className="fw-600 fs-13 mb-12 text-gold" style={{ textTransform: "uppercase" }}> Travel & Accommodation Rider</h3>
          <div className="text-muted fs-11 mb-12" data-testid="travel-cost-disclosure">
            {travelCostDisclosure(pkg, rider)}
          </div>
          {pkg.travel_required && (
            <div className="flex justify-between mb-8"><span className="text-muted">Flight / Travel</span><span>{pkg.flight_class || "economy"} · {pkg.team_size || 1} person(s)</span></div>
          )}
          {pkg.accommodation_required && (
            <div className="flex justify-between mb-8"><span className="text-muted">Accommodation</span><span>{pkg.hotel_category || "3-star"} · {pkg.team_size || 1} person(s)</span></div>
          )}
          {pkg.arrival_buffer_days ? (
            <div className="flex justify-between mb-8"><span className="text-muted">Arrival buffer</span><span>{pkg.arrival_buffer_days} day(s) prior</span></div>
          ) : null}
          {pkg.local_transport_required && <div className="flex justify-between mb-8"><span className="text-muted">Local transport</span><span>Required</span></div>}
          {pkg.meals_required && <div className="flex justify-between mb-8"><span className="text-muted">Meals</span><span>Required during stay</span></div>}
          {pkg.travel_notes && (
            <div className="mt-8">
              <div className="text-muted fs-12 mb-4">Additional notes</div>
              <div className="fs-13">{pkg.travel_notes}</div>
            </div>
          )}
        </div>
      )}

      <div className="card card-pad mb-16">
        <h3 className="fw-600 fs-13 mb-12 text-gold" style={{ textTransform: "uppercase" }}>Event Details</h3>
        <div className="flex justify-between mb-8"><span className="text-muted">Date</span><span>{form.event_date} · {form.event_time}</span></div>
        <div className="flex justify-between mb-8"><span className="text-muted">Venue</span><span>{form.venue}, {form.city}</span></div>
        <div className="flex justify-between"><span className="text-muted">Type</span><span>{form.event_type}</span></div>
        {(form.special_instructions || "").trim() && (
          <div className="mt-12" data-testid="review-special-instructions">
            <div className="text-muted fs-11 mb-4" style={{ textTransform: "uppercase", letterSpacing: 1 }}>Special Instructions</div>
            <div className="fs-13" style={{ whiteSpace: "pre-line" }}>{form.special_instructions}</div>
          </div>
        )}
      </div>

      {/* Rider block — questionnaire-sourced, only for outstation. Keep this
          above the acknowledgement so customers understand the requirements
          before agreeing to the travel terms. */}
      {isOutstation && (
        <div className="mb-16" data-testid="review-rider-block">
          <TravelRiderCard rider={displayRider} artistCity={undefined} eventCity={form.city} />
        </div>
      )}

      {eventHasOutstation && (
        <div className="card card-pad mb-16" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.35)" }} data-testid="review-outstation-notice">
          <div className="fw-700 text-gold fs-13 mb-8" style={{ textTransform: "uppercase", letterSpacing: 1 }}> Outstation Travel Terms</div>
          {travelPolicyRows.map((row) => (
            <div key={row.key} className="mb-8" data-testid={`review-travel-policy-${row.key}`}>
              <div className="fw-600 fs-12">{row.artistName}{row.packageName ? ` · ${row.packageName}` : ""}</div>
              <div className="fs-13" style={{ lineHeight: 1.5, whiteSpace: "pre-line" }}>
                {buildOutstationNotice(row.package, row.rider)}
              </div>
            </div>
          ))}
          {requiresTravelTermsAck && (
            <label className="flex items-start gap-8" data-testid="travel-terms-ack">
              <input
                type="checkbox"
                checked={travelTermsAcknowledged}
                onChange={(e) => {
                  set("travel_terms_acknowledged", e.target.checked);
                  set("travel_ack", e.target.checked);
                  set("outstation_ack", e.target.checked);
                }}
                data-testid="travel-terms-ack-checkbox"
              />
              <span className="fs-12">I have reviewed these travel terms and agree to settle the listed additional costs directly with the artist.</span>
            </label>
          )}
          {travelTermsAcknowledged && visibleTechRiderFile?.mime === "application/pdf" && (
            <div className="mt-12" data-testid="travel-tech-rider">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={previewTechRider}
                disabled={openingTechRider}
                data-testid="travel-tech-rider-link"
              >
                {openingTechRider ? "Opening Tech Rider…" : `Preview ${visibleTechRiderFile.filename || "artist's Tech Rider"} (PDF)`}
              </button>
              {techRiderPreviewError && (
                <div className="text-muted fs-11 mt-8" role="alert" data-testid="travel-tech-rider-error">
                  {techRiderPreviewError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mandatory Terms & Conditions declaration (Iter 52.5). */}
      <div className="card card-pad mb-16" style={{ borderColor: form.tnc_accepted ? "rgba(110,231,168,0.3)" : "rgba(255,120,120,0.28)" }} data-testid="review-tnc-block">
        <h3 className="fw-600 fs-13 mb-8" style={{ textTransform: "uppercase", color: form.tnc_accepted ? "#6ee7a8" : "#ffd270" }}>
           Declaration
        </h3>
        <label className="flex items-start gap-8" style={{ lineHeight: 1.55 }}>
          <input
            type="checkbox"
            checked={!!form.tnc_accepted}
            onChange={(e) => set("tnc_accepted", e.target.checked)}
            data-testid="tnc-accept-checkbox"
            style={{ marginTop: "var(--space-1)", flex: "none" }}
          />
          <span className="fs-12">
            I confirm that the details above are accurate and I have read and agree to BookTalent's{" "}
            <a href="/page/terms" target="_blank" rel="noreferrer" className="text-gold" data-testid="tnc-link-terms">Terms & Conditions</a>,{" "}
            <a href="/page/privacy" target="_blank" rel="noreferrer" className="text-gold" data-testid="tnc-link-privacy">Privacy Policy</a>{" "}
            and{" "}
            <a href="/page/refund-policy" target="_blank" rel="noreferrer" className="text-gold" data-testid="tnc-link-refund">Cancellation & Refund Policy</a>.
            I acknowledge that BookTalent collects only the 5% Platform Service Fee here — the artist's package fee is paid directly to the artist as per the signed agreement.
          </span>
        </label>
      </div>

      <div className="flex justify-between mt-24">
        <button className="btn btn-ghost" onClick={onBack} data-testid="step4-back">← Back</button>
        <button
          className="btn btn-gold"
          onClick={handleNext}
          disabled={nextDisabled}
          data-testid="step4-next"
          title={nextDisabled ? "Please tick the required boxes above" : undefined}
        >
           Proceed to Payment →
        </button>
      </div>
    </div>
  );
}
